import type { MyWorkspace } from "@closer/shared";

/**
 * Erişilemeyen çalışma alanından GÜVENLİ ÇIKIŞ kararı (CLOS-81 `[044]`).
 *
 * ### Neden saf bir fonksiyon
 *
 * Aynı soruyu üç yer soruyor: uygulama açılışı, doğrudan URL girişi ve oturum ortasında
 * gelen erişim kaybı. Karar guard'ın içine gömülseydi üçünde de ayrı ayrı yazılır ve biri
 * unutulurdu — kartın *"doğrudan URL girişleri de aynı kurala uyar"* kriteri tam olarak
 * bu tür bir kaçağı hedefliyor.
 *
 * ### Bu fonksiyon erişim VERMEZ
 *
 * Kartın bağımlılık sınırı açık: *"Bu kart erişim sağlamaz; yalnız erişimi kaybedilmiş
 * veya geçersiz bağlamdan güvenli çıkışı yönetir."* Seçilebilirlik kararı sunucudan gelen
 * `selectable` alanında (`[042]`) ve burada yeniden yorumlanmıyor.
 */

/** Erişimin neden kaybedildiği — kullanıcıya gösterilecek mesajın kaynağı. */
export type KayipSebebi = "suspended" | "ended" | "bilinmiyor";

export type SafeWorkspaceDecision =
  /** İstenen alan erişilebilir; hiçbir şey değişmiyor. */
  | { tur: "gecerli"; tenantId: string }
  /** İstenen alan erişilemez; şu alana düşülüyor ve kullanıcıya açıklanıyor. */
  | {
      tur: "yedek";
      tenantId: string;
      /** Geçilen alanın adı — mesaj nereye düşüldüğünü doğru söylemek zorunda. */
      yedekAd: string;
      /** Kaybedilen alanın ADI — mesajda kullanılır. Liste bilmiyorsa `null`. */
      kaybedilenAd: string | null;
      sebep: KayipSebebi;
    }
  /** Gidilecek güvenli bir alan yok ya da hiç seçim yapılmamış: seçim ekranı. */
  | { tur: "sec" };

/**
 * Erişim kaybının kullanıcıya gösterilecek açıklaması (kart kriteri: *"erişim kaybı
 * anlaşılır mesajla açıklanır"*).
 *
 * Metin BURADA çünkü saf ve test edilebilir; bileşenin içine gömülseydi üç çağrı yerinde
 * üç farklı cümle doğardı. Alanın yalnız ADI kullanılıyor — kartın çalışma zamanı koşulu
 * adın dışındaki hiçbir metadatanın gösterilmemesini istiyor.
 */
export function erisimKaybiMesaji(karar: Extract<SafeWorkspaceDecision, { tur: "yedek" }>): string {
  const nesne = karar.kaybedilenAd
    ? `"${karar.kaybedilenAd}" çalışma alanına`
    : "önceki çalışma alanınıza";
  /**
   * Nereye düşüldüğü ADIYLA söyleniyor. "Kişisel alanınıza geçildi" diye sabitlemek,
   * kişisel alanın da erişilemez olduğu (ve başka bir alana düşüldüğü) durumda kullanıcıya
   * yanlış bilgi verirdi.
   */
  const hedef = `"${karar.yedekAd}" alanına geçildi.`;
  switch (karar.sebep) {
    case "suspended":
      return `${nesne} erişiminiz askıya alındı. ${hedef}`;
    case "ended":
      return `${nesne} üyeliğiniz sona erdi. ${hedef}`;
    default:
      return `${nesne} artık erişiminiz yok. ${hedef}`;
  }
}

/**
 * Yedek alan seçimi: KİŞİSEL ALAN tercih edilir.
 *
 * Kart *"uygun başka alana veya kişisel alana yönlendirilir"* diyerek ikisine de izin
 * veriyor. Kişisel alan seçildi çünkü deterministik bir güvenli liman: kullanıcının
 * kendisine ait, askıya alınamaz ve `[042]` varlığını garanti ediyor. "Tercih edilen
 * alan" için bugün bir sinyal yok — en son kullanılan alan zaten erişimi kaybedilen.
 *
 * Kişisel alan da erişilemezse (beklenmez; fail-closed) erişilebilir ilk alana düşülür.
 */
function yedekAlan(alanlar: readonly MyWorkspace[]): MyWorkspace | undefined {
  return (
    alanlar.find((a) => a.type === "individual" && a.selectable) ??
    alanlar.find((a) => a.selectable)
  );
}

function sebepBul(bulunan: MyWorkspace | undefined): KayipSebebi {
  if (bulunan?.status === "suspended") return "suspended";
  if (bulunan?.status === "ended") return "ended";
  // Liste alanı hiç içermiyor (bayat id, başka hesapla giriş) ya da beklenmeyen bir durum.
  return "bilinmiyor";
}

export function resolveSafeWorkspace({
  alanlar,
  istenen,
}: {
  alanlar: readonly MyWorkspace[];
  istenen: string | null;
}): SafeWorkspaceDecision {
  /**
   * Hiç seçim yok: kaybedilmiş bir erişim de yok.
   *
   * ⚠ TEK ORGANİZASYON KENDİLİĞİNDEN SEÇİLİYOR — saha bulgusu (4 Eylül 2026). Kullanıcı
   * her yeni tarayıcıda seçim ekranıyla karşılaşıyordu ve tek bürosu olan biri için o
   * ekran gerçek bir soru sormuyor: kişisel alan ile o büro arasında seçim yaptırıyor,
   * cevabı zaten belli. Girişten sonra ilk görülen şey bir form oluyordu.
   *
   * ⚠ SEÇİM EKRANI KALDIRILMADI: iki ya da daha fazla organizasyonu olan kullanıcıya
   * hâlâ soruluyor, çünkü orada hangisi olduğu GERÇEKTEN bilinmiyor ve yanlış alana
   * düşmek başka bir müvekkilin verisine bakmak demek. Üst çubuktaki değiştirici de
   * yerinde kalıyor.
   *
   * ⚠ Yalnız kişisel alanı olan kullanıcı da seçim ekranını görüyor: onun için o ekran
   * "organizasyon oluştur" yolunu taşıyor ve anlamlı.
   */
  if (!istenen) {
    const organizasyonlar = alanlar.filter((a) => a.type !== "individual" && a.selectable);
    if (organizasyonlar.length === 1) {
      return { tur: "gecerli", tenantId: organizasyonlar[0]!.tenantId };
    }
    return { tur: "sec" };
  }

  const bulunan = alanlar.find((a) => a.tenantId === istenen);
  if (bulunan?.selectable) return { tur: "gecerli", tenantId: istenen };

  const yedek = yedekAlan(alanlar);
  /**
   * ⚠ Gidilecek yer yoksa "sec" DÖNMEK ZORUNDA. Uydurma bir hedef seçilseydi guard onu
   * bir sonraki render'da yine reddeder ve yönlendirme DÖNGÜSÜ doğardı.
   */
  if (!yedek) return { tur: "sec" };

  return {
    tur: "yedek",
    tenantId: yedek.tenantId,
    yedekAd: yedek.name,
    /**
     * Yalnız AD taşınıyor. Kartın çalışma zamanı koşulu: *"Erişilemeyen alanın adı
     * dışındaki hassas metadatası, kayıtları veya permission bilgileri kullanıcıya
     * gösterilmez."*
     */
    kaybedilenAd: bulunan?.name ?? null,
    sebep: sebepBul(bulunan),
  };
}
